/**
 * filemakerIntegration.ts - FileMaker WebViewer Integration
 * 
 * CHANGE HISTORY:
 * - 251128_1238 danielbeer: Created FileMaker WebViewer JS integration
 * 
 * Features:
 * - Receive commands from FileMaker via window.FileMaker
 * - Send results back to FileMaker
 * - Inject pipeline options from FileMaker scripts
 * - Trigger actions from FileMaker
 */

import type { MapperPipeline, DataSet } from '../types/index';

/**
 * FileMaker command structure
 */
export interface FileMakerCommand {
  action: 
    | 'loadPipeline'
    | 'savePipeline'
    | 'setPipelineOptions'
    | 'addDataset'
    | 'removeDataset'
    | 'enableDataset'
    | 'disableDataset'
    | 'exportJSON'
    | 'importJSON'
    | 'autoMatch'
    | 'getPipelineState';
  
  payload?: any;
  callbackId?: string;
}

/**
 * FileMaker response structure
 */
export interface FileMakerResponse {
  success: boolean;
  data?: any;
  error?: string;
  callbackId?: string;
}

/**
 * FileMaker integration manager
 */
export class FileMakerIntegration {
  private pipeline: MapperPipeline | null = null;
  private updateCallback: ((pipeline: MapperPipeline) => void) | null = null;
  private commandHandlers: Map<string, (payload: any) => Promise<any>> = new Map();

  constructor() {
    this.setupGlobalInterface();
  }

  /**
   * Setup global window.FileMaker interface
   */
  private setupGlobalInterface() {
    // Create FileMaker namespace if it doesn't exist
    if (typeof window !== 'undefined') {
      (window as any).FileMaker = (window as any).FileMaker || {};
      
      // Expose command handler
      (window as any).FileMaker.executeMapprCommand = this.executeCommand.bind(this);
      
      // Expose result callback
      (window as any).FileMaker.MapprReady = true;
      
      console.log('[FileMakerIntegration] Global interface ready');
    }
  }

  /**
   * Set current pipeline
   */
  setPipeline(pipeline: MapperPipeline) {
    this.pipeline = pipeline;
  }

  /**
   * Set update callback
   */
  setUpdateCallback(callback: (pipeline: MapperPipeline) => void) {
    this.updateCallback = callback;
  }

  /**
   * Register custom command handler
   */
  registerHandler(action: string, handler: (payload: any) => Promise<any>) {
    this.commandHandlers.set(action, handler);
  }

  /**
   * Execute command from FileMaker
   */
  async executeCommand(commandJSON: string): Promise<string> {
    try {
      const command: FileMakerCommand = JSON.parse(commandJSON);
      console.log('[FileMakerIntegration] Executing command:', command.action);

      let result: any;

      // Check for custom handler first
      if (this.commandHandlers.has(command.action)) {
        const handler = this.commandHandlers.get(command.action)!;
        result = await handler(command.payload);
      } else {
        // Built-in handlers
        switch (command.action) {
          case 'loadPipeline':
            result = await this.handleLoadPipeline(command.payload);
            break;
          
          case 'savePipeline':
            result = await this.handleSavePipeline(command.payload);
            break;
          
          case 'setPipelineOptions':
            result = await this.handleSetPipelineOptions(command.payload);
            break;
          
          case 'addDataset':
            result = await this.handleAddDataset(command.payload);
            break;
          
          case 'removeDataset':
            result = await this.handleRemoveDataset(command.payload);
            break;
          
          case 'enableDataset':
            result = await this.handleEnableDataset(command.payload, true);
            break;
          
          case 'disableDataset':
            result = await this.handleEnableDataset(command.payload, false);
            break;
          
          case 'getPipelineState':
            result = this.pipeline;
            break;
          
          default:
            throw new Error(`Unknown action: ${command.action}`);
        }
      }

      const response: FileMakerResponse = {
        success: true,
        data: result,
        callbackId: command.callbackId
      };

      return JSON.stringify(response);
    } catch (error) {
      const response: FileMakerResponse = {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };

      return JSON.stringify(response);
    }
  }

  /**
   * Send result back to FileMaker
   */
  sendToFileMaker(data: any, scriptName?: string) {
    if (typeof window !== 'undefined' && (window as any).FileMaker?.PerformScript) {
      const dataJSON = JSON.stringify(data);
      (window as any).FileMaker.PerformScript(scriptName || 'HandleMapprResult', dataJSON);
    }
  }

  /**
   * Handle load pipeline
   */
  private async handleLoadPipeline(_payload: { pipelineId: string }): Promise<MapperPipeline | null> {
    // This would typically load from Cachr using payload.pipelineId
    // For now, return current pipeline
    return this.pipeline;
  }

  /**
   * Handle save pipeline
   */
  private async handleSavePipeline(payload: { pipeline: MapperPipeline }): Promise<boolean> {
    if (payload.pipeline) {
      this.pipeline = payload.pipeline;
      if (this.updateCallback) {
        this.updateCallback(payload.pipeline);
      }
    }
    return true;
  }

  /**
   * Handle set pipeline options
   */
  private async handleSetPipelineOptions(payload: Partial<MapperPipeline>): Promise<MapperPipeline> {
    if (!this.pipeline) {
      throw new Error('No pipeline loaded');
    }

    this.pipeline = {
      ...this.pipeline,
      ...payload
    };

    if (this.updateCallback) {
      this.updateCallback(this.pipeline);
    }

    return this.pipeline;
  }

  /**
   * Handle add dataset
   */
  private async handleAddDataset(payload: { dataset: DataSet }): Promise<MapperPipeline> {
    if (!this.pipeline) {
      throw new Error('No pipeline loaded');
    }

    this.pipeline = {
      ...this.pipeline,
      datasets: [...this.pipeline.datasets, payload.dataset]
    };

    if (this.updateCallback) {
      this.updateCallback(this.pipeline);
    }

    return this.pipeline;
  }

  /**
   * Handle remove dataset
   */
  private async handleRemoveDataset(payload: { datasetId: string }): Promise<MapperPipeline> {
    if (!this.pipeline) {
      throw new Error('No pipeline loaded');
    }

    this.pipeline = {
      ...this.pipeline,
      datasets: this.pipeline.datasets.filter(ds => ds.id !== payload.datasetId)
    };

    if (this.updateCallback) {
      this.updateCallback(this.pipeline);
    }

    return this.pipeline;
  }

  /**
   * Handle enable/disable dataset
   */
  private async handleEnableDataset(payload: { datasetId: string }, enabled: boolean): Promise<MapperPipeline> {
    if (!this.pipeline) {
      throw new Error('No pipeline loaded');
    }

    this.pipeline = {
      ...this.pipeline,
      datasets: this.pipeline.datasets.map(ds =>
        ds.id === payload.datasetId ? { ...ds, enabled } : ds
      )
    };

    if (this.updateCallback) {
      this.updateCallback(this.pipeline);
    }

    return this.pipeline;
  }
}

/**
 * Singleton instance
 */
export const fileMakerIntegration = new FileMakerIntegration();

/**
 * Example FileMaker script to call from FM:
 * 
 * # In FileMaker, use "Perform JavaScript in Web Viewer" step:
 * 
 * ```javascript
 * window.FileMaker.executeMapprCommand(JSON.stringify({
 *   action: 'setPipelineOptions',
 *   payload: {
 *     showFieldTypes: true,
 *     showSampleData: false
 *   },
 *   callbackId: 'my-callback-123'
 * }))
 * ```
 * 
 * # To get result back in FileMaker:
 * Create a script called "HandleMapprResult" that receives the JSON result
 */
